import React, { useState } from "react"

import { gql, useQuery } from "hooks/urql"

import Checkbox from "components/Forms/Checkbox"
import LocalTime from "components/LocalTime"
import Pagination from "components/Pagination"
import UrqlLoadingIndicator from "components/WithLoadingIndicator/urql"

const PackageParents = () => {
  const [parentPage, setParentPage] = useState(1)
  const [zeroBalanceOnly, setZeroBalanceOnly] = useState(false)
  const [lowBalanceOnly, setLowBalanceOnly] = useState(false)

  const [result] = useQuery({
    query: packageParentsQuery,
    variables: { parentPage, zeroBalanceOnly, lowBalanceOnly },
  })

  return (
    <UrqlLoadingIndicator result={result}>
      {({ data: { packageParents } }) => (
        <>
          <div className="mb-2 flex gap-6 ">
            <Checkbox
              name="zeroBalanceOnly"
              value={zeroBalanceOnly}
              checked={zeroBalanceOnly}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setLowBalanceOnly(false)
                setZeroBalanceOnly(e.target.checked)
              }}
            >
              Zero Balance Only
            </Checkbox>
            <Checkbox
              name="lowBalanceOnly"
              value={lowBalanceOnly}
              checked={lowBalanceOnly}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setZeroBalanceOnly(false)
                setLowBalanceOnly(e.target.checked)
              }}
            >
              Low Balance Only
            </Checkbox>
          </div>

          <table className="table">
            <thead className="text-primary">
              <tr>
                <th>Parent Name</th>
                <th>Student Info</th>
                <th>Subjects</th>
                <th>Matched Tutors</th>
                <th>Prepaid Hours Balance</th>
                <th>Last Package Purcahsed</th>
              </tr>
            </thead>
            <tbody>
              {packageParents.data.map(parent => (
                <tr key={parent.id}>
                  <td>
                    <a
                      href={parent.showPath}
                      className="font-semibold"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {parent.fullName}
                    </a>
                  </td>
                  <td>
                    {parent.students.map(student => (
                      <div key={student.id}>
                        <a
                          href={student.showPath}
                          className="font-semibold"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {student.fullName}
                        </a>
                        <div>
                          {student.grade?.name}{" "}
                          {student.school && `at ${student.school.name}`}
                        </div>
                        <div>
                          {student.foreignLanguages
                            .map(lang => lang.name)
                            .join(", ")}
                        </div>
                      </div>
                    ))}
                  </td>
                  <td>
                    {parent.students.map(student => (
                      <div key={student.id}>
                        <span className="font-medium">{student.firstName}</span>
                        &nbsp;&mdash;&nbsp;
                        {student.subjects
                          .map(subject => subject.name)
                          .join(", ")}
                      </div>
                    ))}
                  </td>
                  <td>
                    {parent.students.map(student => (
                      <div key={student.id}>
                        <span className="font-medium">{student.firstName}</span>
                        &nbsp;&mdash;&nbsp;
                        {student.matchedUsers.data.map((tutor, idx) => (
                          <span key={tutor.id}>
                            <a
                              href={tutor.showPath}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {tutor.fullName}
                            </a>
                            {idx < student.matchedUsers.data.length - 1 && ", "}
                          </span>
                        ))}
                      </div>
                    ))}
                  </td>
                  <td>{parent.prepaidHours}</td>
                  <td>
                    <LocalTime
                      omitTime
                      timestamp={parent.lastPackagePurchased}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <Pagination
            totalPages={packageParents.totalPages}
            currentPage={packageParents.currentPage}
            changePage={setParentPage}
          />
        </>
      )}
    </UrqlLoadingIndicator>
  )
}

const packageParentsQuery = gql`
  query PackageParentsQuery(
    $parentPage: Int!
    $zeroBalanceOnly: Boolean
    $lowBalanceOnly: Boolean
  ) {
    packageParents(
      page: $parentPage
      zeroBalanceOnly: $zeroBalanceOnly
      lowBalanceOnly: $lowBalanceOnly
    ) {
      totalPages
      currentPage
      data {
        id
        type
        showPath
        fullName
        prepaidHours
        lastPackagePurchased
        students {
          id
          firstName
          fullName
          showPath
          grade {
            id
            name
          }
          foreignLanguages {
            id
            name
          }
          school {
            id
            showPath
            name
          }
          subjects {
            id
            name
          }
          matchedUsers {
            data {
              id
              fullName
              showPath
            }
          }
        }
      }
    }
  }
`

export default PackageParents
